import React, { ReactElement, useEffect, useState } from 'react'

import PageHeader from '@instabox/internal-web-ui/components/PageHeader'
import Table from '@instabox/internal-web-ui/components/Table'
import ErrorIcon from '@instabox/internal-web-ui/icons/ErrorIcon'
import { Device as BaseDevice } from '@instabox/lockers-update-common/types'
import { differenceInDays, formatISO9075 } from 'date-fns'
import { orderBy } from 'lodash'

import useDevices from '../../hooks/useDevices'
import TableHeaderButton from '../components/TableHeaderButton'

import DeviceActions from './DeviceActions'
import NoAgentDevicesTable from './NoAgentDevicesTable'

interface Device extends BaseDevice {
  synced: boolean
}

interface UpdateWarningProps {
  synced: boolean
}

const UpdateWarning: React.FC<UpdateWarningProps> = ({ synced }) => {
  if (synced === true) {
    return null
  }

  return <ErrorIcon color="red" data-testid="updateWarningIcon" />
}

const hasDeviceSynced = (updatedAt?: Date) => {
  if (updatedAt === undefined) {
    return false
  }

  return differenceInDays(new Date(), new Date(updatedAt)) < 1
}

interface SortingState {
  sortingKey: keyof Device
  reversed: boolean
}

function Devices(): ReactElement {
  const { devices, loading, reload } = useDevices()
  const [localDevices, setLocalDevices] = useState<Device[]>([])
  const [sortingState, setSortingState] = useState<SortingState>()

  const handleHeaderClicked = (sortingKey: keyof Device) => {
    if (sortingState?.sortingKey === sortingKey) {
      setSortingState({
        sortingKey,
        reversed: !sortingState.reversed,
      })
      return
    }

    setSortingState({
      sortingKey,
      reversed: false,
    })
  }

  useEffect(() => {
    const ds = devices.map((d) => ({
      ...d,
      synced: hasDeviceSynced(d.updatedAt),
    }))

    if (sortingState === undefined) {
      setLocalDevices(ds)
      return
    }

    const sorted = orderBy(ds, (device) => device[sortingState.sortingKey], sortingState.reversed ? 'asc' : 'desc')
    setLocalDevices(sorted)
  }, [devices, sortingState])

  const syncedDevices = localDevices.filter((d) => d.synced)
  const notSynced = (
    Math.round(((localDevices.length - syncedDevices.length) / localDevices.length) * 1000) / 10.0
  ).toFixed(1)

  return (
    <div>
      <PageHeader title={`Devices`} tag="h2" />

      <div className="mb-4 d-flex align-items-center">
        <div className="me-4" title="Devices without sync" data-testid="sync-indicator">
          <ErrorIcon></ErrorIcon>
          <span>{` : ${notSynced}% ( ${localDevices.length - syncedDevices.length} )`}</span>
        </div>
      </div>

      <PageHeader title={`Devices with agent (${localDevices.length})`} size="md" tag="h2" />
      <Table
        className="mb-4"
        isLoading={loading}
        headers={{
          updateWarning: (
            <TableHeaderButton
              data-testid="updateWarningHeader"
              icon={<ErrorIcon />}
              onClick={() => handleHeaderClicked('updatedAt')}
            />
          ),
          deviceName: (
            <TableHeaderButton
              data-testid="deviceNameHeader"
              label="Device Name"
              onClick={() => handleHeaderClicked('deviceName')}
            />
          ),
          deviceId: (
            <TableHeaderButton
              data-testid="deviceIdHeader"
              label="Device Id"
              onClick={() => handleHeaderClicked('deviceId')}
            />
          ),
          updatedAt: (
            <TableHeaderButton
              data-testid="updatedAtHeader"
              label="Updated at"
              onClick={() => handleHeaderClicked('updatedAt')}
            />
          ),
          actions: '',
        }}
        rows={localDevices.map((device) => ({
          ...device,
          updateWarning: <UpdateWarning synced={device.synced} />,
          updatedAt: formatISO9075(new Date(device.updatedAt as Date)),
          actions: <DeviceActions deviceId={device.deviceId} favorite={device.favorite} reloadDevices={reload} />,
        }))}
      />

      <NoAgentDevicesTable />
    </div>
  )
}

export default Devices
